(() => {
  'use strict';

  const SELECTORS = {
    root: '[data-nsy-variant-product]',
    form: '[data-nsy-product-form]',
    variantsJson: '[data-nsy-variants-json]',
    variantId: '[data-nsy-variant-id]',
    variantRadio: '.nsy-variant-radio',
    variantGroup: '.nsy-variant-group',
    selectedValue: '[data-nsy-selected-value]',
    salePrice: '[data-nsy-sale-price]',
    comparePrice: '[data-nsy-compare-price]',
    offBadge: '[data-nsy-off-badge]',
    addButton: '[data-nsy-add-cart]',
    status: '[data-nsy-variant-status]',
    gallery: '[data-nsy-gallery]',
    mainImage: '[data-nsy-main-image]',
    mainWrap: '[data-nsy-main-wrap]',
    thumbnails: '[data-nsy-thumbnails]',
    thumbnail: '[data-nsy-thumb]',
    openGallery: '[data-nsy-open-gallery]',
    quantityInput: '[data-nsy-qty-input]',
    quantityMinus: '[data-nsy-qty-minus]',
    quantityPlus: '[data-nsy-qty-plus]',
    tabs: '[data-nsy-tabs]',
    tabButton: '[data-tab-btn]',
    tabPanel: '[data-tab-panel]'
  };

  class NSYVariantProduct {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector(SELECTORS.form);
      this.variantsElement = root.querySelector(SELECTORS.variantsJson);
      this.variantIdInput = root.querySelector(SELECTORS.variantId);
      this.salePrice = root.querySelector(SELECTORS.salePrice);
      this.comparePrice = root.querySelector(SELECTORS.comparePrice);
      this.offBadge = root.querySelector(SELECTORS.offBadge);
      this.addButton = root.querySelector(SELECTORS.addButton);
      this.status = root.querySelector(SELECTORS.status);
      this.gallery = root.querySelector(SELECTORS.gallery);
      this.mainImage = root.querySelector(SELECTORS.mainImage);
      this.mainWrap = root.querySelector(SELECTORS.mainWrap);
      this.thumbnailsWrap = root.querySelector(SELECTORS.thumbnails);
      this.quantityInput = root.querySelector(SELECTORS.quantityInput);
      this.productUrl = root.dataset.productUrl || window.location.pathname;

      this.variants = [];
      this.currentVariant = null;
      this.galleryImages = [];
      this.currentImageIndex = 0;
      this.imageRequestId = 0;
      this.touchStartX = 0;
      this.modal = null;
      this.lastFocusedElement = null;

      this.boundPopState = this.onPopState.bind(this);
      this.boundKeyDown = this.onKeyDown.bind(this);
    }

    init() {
      if (!this.form || !this.variantsElement || !this.variantIdInput) return;
      if (this.root.dataset.nsyVariantReady === 'true') return;

      this.root.dataset.nsyVariantReady = 'true';
      this.readVariants();
      if (!this.variants.length) return;

      this.createModal();
      this.bindEvents();
      this.initializeFromUrl();
      this.initializeTabs();
      this.syncQuantity();
    }

    readVariants() {
      try {
        const parsed = JSON.parse(this.variantsElement.textContent.trim());
        this.variants = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('NISAYI Variant Product: invalid variant JSON.', error);
        this.variants = [];
      }
    }

    bindEvents() {
      this.form.addEventListener('change', (event) => {
        if (!event.target.matches(SELECTORS.variantRadio)) return;
        this.onOptionChange();
      });

      this.root.addEventListener('click', (event) => {
        const minus = event.target.closest(SELECTORS.quantityMinus);
        if (minus && this.root.contains(minus)) {
          event.preventDefault();
          this.changeQuantity(-1);
          return;
        }

        const plus = event.target.closest(SELECTORS.quantityPlus);
        if (plus && this.root.contains(plus)) {
          event.preventDefault();
          this.changeQuantity(1);
          return;
        }

        const tabButton = event.target.closest(SELECTORS.tabButton);
        if (tabButton && this.root.contains(tabButton)) {
          event.preventDefault();
          this.activateTab(tabButton);
        }
      });

      /* Capture prevents the global legacy gallery from handling this isolated PDP. */
      if (this.gallery) {
        this.gallery.addEventListener('click', (event) => {
          const thumb = event.target.closest(SELECTORS.thumbnail);
          const opener = event.target.closest(SELECTORS.openGallery);
          const main = event.target.closest(SELECTORS.mainImage);

          if (thumb && this.gallery.contains(thumb)) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            const index = this.getThumbnailIndex(thumb);
            if (index >= 0) this.changeMainImage(index);
            return;
          }

          if ((opener && this.gallery.contains(opener)) || (main && this.gallery.contains(main))) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            this.openModal();
          }
        }, true);
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener('change', () => this.syncQuantity());
        this.quantityInput.addEventListener('input', () => this.syncQuantity());
      }

      window.addEventListener('popstate', this.boundPopState);
      document.addEventListener('keydown', this.boundKeyDown);
    }

    initializeFromUrl() {
      const requestedId = Number(new URL(window.location.href).searchParams.get('variant'));
      const requestedVariant = requestedId
        ? this.variants.find((variant) => Number(variant.id) === requestedId)
        : null;

      const inputVariant = this.variants.find(
        (variant) => Number(variant.id) === Number(this.variantIdInput.value)
      );

      const initialVariant = requestedVariant || inputVariant || this.variants[0];
      this.selectVariantOptions(initialVariant);
      this.applyVariant(initialVariant, { updateUrl: false, replaceUrl: true });
    }

    onOptionChange() {
      this.updateSelectedLabels();
      const selectedOptions = this.getSelectedOptions();
      const variant = this.variants.find((item) =>
        selectedOptions.every((value, index) => item.options[index] === value)
      );

      if (!variant) {
        this.setUnavailableState();
        return;
      }

      this.applyVariant(variant, { updateUrl: true, replaceUrl: false });
    }

    onPopState() {
      const variantId = Number(new URL(window.location.href).searchParams.get('variant'));
      const variant = this.variants.find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.selectVariantOptions(variant);
      this.applyVariant(variant, { updateUrl: false, replaceUrl: false });
    }

    getSelectedOptions() {
      return Array.from(this.form.querySelectorAll(SELECTORS.variantGroup)).map((group) => {
        const checked = group.querySelector(`${SELECTORS.variantRadio}:checked`);
        return checked ? checked.value : null;
      });
    }

    selectVariantOptions(variant) {
      if (!variant || !Array.isArray(variant.options)) return;

      const groups = Array.from(this.form.querySelectorAll(SELECTORS.variantGroup));
      groups.forEach((group, index) => {
        const expectedValue = variant.options[index];
        const radios = Array.from(group.querySelectorAll(SELECTORS.variantRadio));
        const target = radios.find((radio) => radio.value === expectedValue);
        if (target) target.checked = true;
      });

      this.updateSelectedLabels();
    }

    getProgressiveComboName(group, selectedValue) {
      const values = Array.from(group.querySelectorAll(SELECTORS.variantRadio))
        .map((radio) => radio.value);

      const selectedIndex = values.indexOf(selectedValue);
      if (selectedIndex === -1) return selectedValue;

      return values.slice(0, selectedIndex + 1).join(' + ');
    }

    updateSelectedLabels() {
      this.form.querySelectorAll(SELECTORS.variantGroup).forEach((group) => {
        const checked = group.querySelector(`${SELECTORS.variantRadio}:checked`);
        const label = group.querySelector(SELECTORS.selectedValue);

        if (checked && label) {
          label.textContent = this.getProgressiveComboName(group, checked.value);
        }
      });
    }

    applyVariant(variant, options = {}) {
      if (!variant) return;
      this.currentVariant = variant;
      this.variantIdInput.value = String(variant.id);

      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.renderGallery(variant);

      if (options.updateUrl) {
        this.updateUrl(variant.id, Boolean(options.replaceUrl));
      } else if (options.replaceUrl) {
        this.updateUrl(variant.id, true);
      }

      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.root.dispatchEvent(new CustomEvent('nsy:variant-changed', {
        bubbles: true,
        detail: { variant }
      }));
    }

    updatePrice(variant) {
      if (this.salePrice) this.salePrice.textContent = this.formatMoney(variant.price);

      const hasComparePrice = Number(variant.compare_at_price) > Number(variant.price);
      if (this.comparePrice) {
        this.comparePrice.hidden = !hasComparePrice;
        this.comparePrice.textContent = hasComparePrice
          ? this.formatMoney(variant.compare_at_price)
          : '';
      }

      if (this.offBadge) {
        this.offBadge.hidden = !hasComparePrice;
        this.offBadge.textContent = hasComparePrice
          ? `${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}% OFF`
          : '';
      }
    }

    updateAvailability(variant) {
      const available = Boolean(variant.available);
      if (this.addButton) {
        this.addButton.disabled = !available;
        this.addButton.setAttribute('aria-disabled', String(!available));
        this.addButton.textContent = available ? 'Add To Cart' : 'Sold Out';
      }

      if (this.status) {
        this.status.textContent = available ? '' : 'This option is currently sold out.';
      }
    }

    setUnavailableState() {
      this.currentVariant = null;
      this.variantIdInput.value = '';
      if (this.addButton) {
        this.addButton.disabled = true;
        this.addButton.setAttribute('aria-disabled', 'true');
        this.addButton.textContent = 'Unavailable';
      }
      if (this.status) this.status.textContent = 'This combination is unavailable.';
    }

    getVariantImages(variant) {
      let images = Array.isArray(variant.gallery)
        ? variant.gallery.filter((image) => image && image.main)
        : [];

      if (!images.length && variant.featured_image && variant.featured_image.main) {
        images = [variant.featured_image];
      }

      return images.map((image) => ({
        thumb: image.thumb || image.main,
        main: image.main,
        zoom: image.zoom || image.main,
        alt: image.alt || variant.title || 'Product image'
      }));
    }

    renderGallery(variant) {
      if (!this.gallery || !this.mainImage || !this.thumbnailsWrap) return;

      const images = this.getVariantImages(variant);
      if (!images.length) return;

      this.galleryImages = images;
      this.currentImageIndex = 0;
      this.imageRequestId += 1;

      this.thumbnailsWrap.innerHTML = images.map((image, index) => `
        <button
          type="button"
          class="nsy-product-thumb${index === 0 ? ' active is-active' : ''}"
          data-nsy-thumb
          data-image="${this.escapeHtml(image.main)}"
          data-popup-image="${this.escapeHtml(image.zoom)}"
          aria-label="View ${this.escapeHtml(variant.title)} image ${index + 1}"
          ${index === 0 ? 'aria-current="true"' : ''}
        >
          <img
            src="${this.escapeHtml(image.thumb)}"
            alt="${this.escapeHtml(image.alt)}"
            loading="lazy"
            width="220"
            height="220"
          >
        </button>
      `).join('');

      this.setMainImage(images[0], false);
      this.updateModalControls();

      this.gallery.dispatchEvent(new CustomEvent('nsy:gallery-updated', {
        bubbles: true,
        detail: { variantId: variant.id, images }
      }));
    }

    getThumbnailIndex(thumbnail) {
      return Array.from(this.thumbnailsWrap.querySelectorAll(SELECTORS.thumbnail)).indexOf(thumbnail);
    }

    async changeMainImage(index) {
      if (!this.galleryImages.length) return;
      const normalized = this.normalizeIndex(index);
      const image = this.galleryImages[normalized];
      const requestId = ++this.imageRequestId;

      this.currentImageIndex = normalized;
      this.setActiveThumbnail(normalized);
      if (this.mainWrap) this.mainWrap.classList.add('is-changing');
      this.mainImage.style.opacity = '0';

      await this.preloadImage(image.main);
      if (requestId !== this.imageRequestId) return;

      this.setMainImage(image, true);
      if (this.mainWrap) this.mainWrap.classList.remove('is-changing');
      this.mainImage.style.opacity = '1';
    }

    setMainImage(image, preserveTransition = true) {
      if (!this.mainImage || !image) return;
      this.mainImage.removeAttribute('srcset');
      this.mainImage.removeAttribute('sizes');
      this.mainImage.src = image.main;
      this.mainImage.alt = image.alt || 'Product image';
      this.mainImage.dataset.nsyZoomImage = image.zoom || image.main;
      if (!preserveTransition) this.mainImage.style.opacity = '1';
    }

    setActiveThumbnail(index) {
      if (!this.thumbnailsWrap) return;
      this.thumbnailsWrap.querySelectorAll(SELECTORS.thumbnail).forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('active', active);
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
    }

    createModal() {
      const id = `NsyVariantModal-${this.root.dataset.sectionId || Math.random().toString(36).slice(2)}`;
      this.modal = document.getElementById(id);
      if (this.modal) return;

      this.modal = document.createElement('div');
      this.modal.id = id;
      this.modal.className = 'nsy-image-modal nsy-variant-image-modal';
      this.modal.setAttribute('data-nsy-variant-modal', '');
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('aria-label', 'Product image gallery');
      this.modal.innerHTML = `
        <button type="button" class="nsy-image-modal__close" data-nsy-variant-modal-close aria-label="Close gallery">×</button>
        <button type="button" class="nsy-image-modal__arrow nsy-image-modal__arrow--prev" data-nsy-variant-modal-prev aria-label="Previous image">←</button>
        <div class="nsy-image-modal__stage" data-nsy-variant-modal-stage>
          <img src="" alt="" data-nsy-variant-modal-image draggable="false">
        </div>
        <button type="button" class="nsy-image-modal__arrow nsy-image-modal__arrow--next" data-nsy-variant-modal-next aria-label="Next image">→</button>
        <div class="nsy-image-modal__counter" data-nsy-variant-modal-counter></div>
      `;
      document.body.appendChild(this.modal);

      this.modalImage = this.modal.querySelector('[data-nsy-variant-modal-image]');
      this.modalCounter = this.modal.querySelector('[data-nsy-variant-modal-counter]');
      this.modalStage = this.modal.querySelector('[data-nsy-variant-modal-stage]');

      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal || event.target.closest('[data-nsy-variant-modal-close]')) {
          event.preventDefault();
          this.closeModal();
          return;
        }
        if (event.target.closest('[data-nsy-variant-modal-prev]')) {
          event.preventDefault();
          this.updateModalImage(this.currentImageIndex - 1);
          return;
        }
        if (event.target.closest('[data-nsy-variant-modal-next]')) {
          event.preventDefault();
          this.updateModalImage(this.currentImageIndex + 1);
        }
      });

      if (this.modalStage) {
        this.modalStage.addEventListener('touchstart', (event) => {
          this.touchStartX = event.changedTouches[0].clientX;
        }, { passive: true });

        this.modalStage.addEventListener('touchend', (event) => {
          const distance = event.changedTouches[0].clientX - this.touchStartX;
          if (Math.abs(distance) < 45) return;
          this.updateModalImage(this.currentImageIndex + (distance > 0 ? -1 : 1));
        }, { passive: true });
      }
    }

    openModal() {
      if (!this.modal || !this.galleryImages.length) return;
      this.lastFocusedElement = document.activeElement;
      this.updateModalImage(this.currentImageIndex, false);
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('nsy-modal-open');
      document.body.style.overflow = 'hidden';
      const close = this.modal.querySelector('[data-nsy-variant-modal-close]');
      if (close) close.focus({ preventScroll: true });
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('active', 'is-changing');
      this.modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('nsy-modal-open');
      document.body.style.overflow = '';
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    async updateModalImage(index, animate = true) {
      if (!this.modalImage || !this.galleryImages.length) return;
      const normalized = this.normalizeIndex(index);
      const image = this.galleryImages[normalized];
      this.currentImageIndex = normalized;

      if (animate) this.modal.classList.add('is-changing');
      await this.preloadImage(image.zoom || image.main);
      this.modalImage.src = image.zoom || image.main;
      this.modalImage.alt = image.alt || 'Product image';
      this.modal.classList.remove('is-changing');
      this.setActiveThumbnail(normalized);
      this.updateModalControls();
    }

    updateModalControls() {
      if (!this.modal) return;
      if (this.modalCounter) {
        this.modalCounter.textContent = this.galleryImages.length
          ? `${this.currentImageIndex + 1} / ${this.galleryImages.length}`
          : '';
      }
      const hideArrows = this.galleryImages.length <= 1;
      const prev = this.modal.querySelector('[data-nsy-variant-modal-prev]');
      const next = this.modal.querySelector('[data-nsy-variant-modal-next]');
      if (prev) prev.hidden = hideArrows;
      if (next) next.hidden = hideArrows;
    }

    onKeyDown(event) {
      if (!this.modal || !this.modal.classList.contains('active')) return;
      if (event.key === 'Escape') this.closeModal();
      if (event.key === 'ArrowLeft') this.updateModalImage(this.currentImageIndex - 1);
      if (event.key === 'ArrowRight') this.updateModalImage(this.currentImageIndex + 1);

      if (event.key === 'Tab') {
        const focusable = Array.from(this.modal.querySelectorAll('button:not([hidden]):not([disabled])'));
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    changeQuantity(delta) {
      if (!this.quantityInput) return;
      const min = Number(this.quantityInput.min || 1);
      const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      const current = Number.parseInt(this.quantityInput.value, 10) || min;
      this.quantityInput.value = String(Math.min(max, Math.max(min, current + delta)));
      this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    syncQuantity() {
      if (!this.quantityInput) return;
      const min = Number(this.quantityInput.min || 1);
      const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
      let value = Number.parseInt(this.quantityInput.value, 10);
      if (!Number.isFinite(value)) value = min;
      this.quantityInput.value = String(Math.min(max, Math.max(min, value)));
    }

    initializeTabs() {
      const tabs = this.root.querySelector(SELECTORS.tabs);
      if (!tabs) return;
      const active = tabs.querySelector(`${SELECTORS.tabButton}.active`) || tabs.querySelector(SELECTORS.tabButton);
      if (active) this.activateTab(active);
    }

    activateTab(button) {
      const tabs = button.closest(SELECTORS.tabs);
      if (!tabs) return;
      const name = button.dataset.tabBtn;
      tabs.querySelectorAll(SELECTORS.tabButton).forEach((item) => {
        const active = item === button;
        item.classList.toggle('active', active);
        item.setAttribute('aria-selected', String(active));
      });
      tabs.querySelectorAll(SELECTORS.tabPanel).forEach((panel) => {
        const active = panel.dataset.tabPanel === name;
        panel.classList.toggle('active', active);
        panel.hidden = !active;
      });
    }

    updateUrl(variantId, replace = false) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      const state = { ...(history.state || {}), nsyVariantId: variantId };
      if (replace) history.replaceState(state, '', url.toString());
      else history.pushState(state, '', url.toString());
    }

    formatMoney(cents) {
      const currency = this.variantsElement.dataset.currency || 'INR';
      const locale = document.documentElement.lang || 'en-IN';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    normalizeIndex(index) {
      return ((index % this.galleryImages.length) + this.galleryImages.length) % this.galleryImages.length;
    }

    preloadImage(url) {
      return new Promise((resolve) => {
        if (!url) {
          resolve(false);
          return;
        }
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = url;
        if (image.complete) resolve(true);
      });
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  function boot(scope = document) {
    scope.querySelectorAll(SELECTORS.root).forEach((root) => {
      const instance = new NSYVariantProduct(root);
      instance.init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();



